import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import { Action, Stack } from "types/generated";
import { AccessResult, NO_ACCESS_MESSAGE, hasAtLeastOneRequiredAction } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import useHasStackAdminAccess from "./useHasStackAdminAccess";

const STACK_STATE_DOWNLOAD_ACCESS_TYPES = [Action.SpaceAdmin, Action.StackStateDownload];

const hasStateDownloadAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(STACK_STATE_DOWNLOAD_ACCESS_TYPES, viewer, spaceId);

type StackForDownload = Pick<Stack, "spaceDetails" | "space" | "isLockedByTemplateDeployment">;

const useCanDownloadStackState = (): ((stack: StackForDownload) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const hasStackAdminAccessFn = useHasStackAdminAccess();

  return useCallback(
    (stack) => {
      if (viewer.admin) {
        return { result: true };
      }

      if (hasStackAdminAccessFn(stack).result) {
        return { result: true };
      }

      if (hasStateDownloadAccess(viewer, stack.spaceDetails?.id || stack.space)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer, hasStackAdminAccessFn]
  );
};

export default useCanDownloadStackState;
